import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCandidates } from '../hooks/useCandidates'
import { STANCE_QUESTIONS, CATEGORIES, computeMatch, loadUserAnswers, hasCompletedQuestionnaire } from '../engine/matchEngine'
import { reviewedDirectory } from '../engine/reviewedBallot'
import MatchBar from '../components/MatchBar'

/** A party we do not hold is said out loud. "{c.party}" over a null rendered
 *  "Beth Macy ()", which reads as a formatting bug rather than missing data. */
function partyLabel(party) {
  const value = typeof party === 'string' ? party.trim() : ''
  return value || 'Party not recorded'
}

export default function Compare() {
  const [searchParams, setSearchParams] = useSearchParams()
  // The id a voter asked for, which may be a superseded lineage carried in an
  // old link, is kept separate from the id actually being displayed.
  const [requestedAId, setRequestedAId] = useState(searchParams.get('a') || '')
  const [requestedBId, setRequestedBId] = useState(searchParams.get('b') || '')
  const { candidates: fetched, loading, error } = useCandidates()
  // One option per person. Two rows exist per reviewed Virginia candidate and
  // both were being offered, under different names and different party
  // strings. See reviewedDirectory for why this is not reviewedCandidatesFor.
  const { candidates, resolveId } = useMemo(() => reviewedDirectory(fetched), [fetched])
  const candidateAId = resolveId(requestedAId)
  const candidateBId = resolveId(requestedBId)
  const completed = hasCompletedQuestionnaire()
  const { weights, stances } = loadUserAnswers()
  const candA = candidates.find(c => c.id === candidateAId)
  const candB = candidates.find(c => c.id === candidateBId)

  // Keep the address bar equal to what is on screen, so the comparison a voter
  // is looking at is the comparison they can send to someone else. Written
  // with the resolved ids, so sharing a link never passes on a dead lineage.
  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (candidateAId) next.set('a', candidateAId); else next.delete('a')
    if (candidateBId) next.set('b', candidateBId); else next.delete('b')
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true })
  }, [candidateAId, candidateBId, searchParams, setSearchParams])

  const matchA = (completed && candA) ? computeMatch(weights, stances, candA) : null
  const matchB = (completed && candB) ? computeMatch(weights, stances, candB) : null

  // Group candidates by state for the dropdown
  const byState = candidates.reduce((acc, c) => {
    if (!acc[c.state]) acc[c.state] = []
    acc[c.state].push(c)
    return acc
  }, {})
  const states = Object.keys(byState).sort()

  function positionLabel(val) {
    if (val === undefined) return { label: 'No data', color: 'text-gray-400' }
    const pct = val * 100
    if (pct >= 70) return { label: 'Strong support', color: 'text-blue-600' }
    if (pct >= 55) return { label: 'Leans support', color: 'text-blue-400' }
    if (pct >= 45) return { label: 'Moderate', color: 'text-gray-500' }
    if (pct >= 30) return { label: 'Leans oppose', color: 'text-red-400' }
    return { label: 'Strong oppose', color: 'text-red-600' }
  }

  return (
    <div className="section max-w-5xl">
      <h1 className="font-display text-3xl text-civitas-navy mb-2">Compare Candidates</h1>
      <p className="text-civitas-silver mb-8">Select two candidates to compare their positions side by side.</p>

      {error && <p role="alert">Unable to load candidate evidence. Please try again.</p>}
      {/* Selectors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        {[
          { label: 'Candidate A', value: candidateAId, onChange: setRequestedAId },
          { label: 'Candidate B', value: candidateBId, onChange: setRequestedBId },
        ].map(({ label, value, onChange }) => (
          <div key={label}>
            <label className="block text-sm font-medium text-civitas-navy mb-2">{label}</label>
            <select
              value={value}
              onChange={e => onChange(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-civitas-navy"
              disabled={loading}
            >
              <option value="">— Select a candidate —</option>
              {states.map(state => (
                <optgroup key={state} label={state}>
                  {byState[state].map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({partyLabel(c.party)})
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        ))}
      </div>

      {/* Comparison table */}
      {candA && candB ? (
        <div>
          {/* Header cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="col-span-1" />
            {[{ cand: candA, match: matchA }, { cand: candB, match: matchB }].map(({ cand, match }) => (
              <div key={cand.id} className="card text-center">
                <h3 className="font-semibold text-civitas-navy">{cand.name}</h3>
                <p className="text-xs text-civitas-silver">{partyLabel(cand.party)} · {cand.state}</p>
                {match && (
                  <div className="mt-2">
                    <div className="text-2xl font-bold text-civitas-navy">{Number.isFinite(match.score) ? match.score + '%' : 'Insufficient recorded evidence'}</div>
                    <div className="text-xs text-civitas-silver">your match</div>
                    <MatchBar score={match.score} animate height="h-2" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Stance comparison rows */}
          <div className="card overflow-hidden p-0">
            <div className="grid grid-cols-3 bg-civitas-navy text-white text-sm font-medium px-4 py-3">
              <span>Policy Question</span>
              <span className="text-center">{candA.name.split(' ')[0]}</span>
              <span className="text-center">{candB.name.split(' ')[0]}</span>
            </div>

            {STANCE_QUESTIONS.map((q, i) => {
              const aVal = candA.position_vector?.[q.id]
              const bVal = candB.position_vector?.[q.id]
              const aLabel = positionLabel(aVal)
              const bLabel = positionLabel(bVal)

              return (
                <div
                  key={q.id}
                  className={`grid grid-cols-3 px-4 py-3 text-sm items-center ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  <span className="font-medium text-civitas-navy">{q.label}</span>
                  <span className={`text-center text-xs font-medium ${aLabel.color}`}>{aLabel.label}</span>
                  <span className={`text-center text-xs font-medium ${bLabel.color}`}>{bLabel.label}</span>
                </div>
              )
            })}
          </div>

          {/* Category match breakdown */}
          {matchA && matchB && (
            <div className="mt-6">
              <h2 className="font-semibold text-civitas-navy mb-4">Match Score by Category</h2>
              <div className="card">
                {CATEGORIES.map(cat => (
                  <div key={cat.id} className="mb-4">
                    <div className="text-sm font-medium text-civitas-navy mb-1">{cat.label}</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs text-civitas-silver mb-1">{candA.name.split(' ')[0]}: {matchA.breakdown[cat.id] == null ? 'Not scored' : matchA.breakdown[cat.id] + '%'}</div>
                        <MatchBar score={matchA.breakdown[cat.id] ?? null} height="h-2" animate />
                      </div>
                      <div>
                        <div className="text-xs text-civitas-silver mb-1">{candB.name.split(' ')[0]}: {matchB.breakdown[cat.id] == null ? 'Not scored' : matchB.breakdown[cat.id] + '%'}</div>
                        <MatchBar score={matchB.breakdown[cat.id] ?? null} height="h-2" animate />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-16 text-civitas-silver">
          {/* An empty selector under "select two candidates" tells a voter
              there is nothing to compare. When the fetch failed, there is a
              list and we could not read it, which is a different sentence. */}
          {loading
            ? 'Loading candidates...'
            : error
              ? 'The candidate list could not be loaded, so no comparison can be shown yet.'
              : 'Select two candidates above to begin comparing.'}
        </div>
      )}
    </div>
  )
}
